import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { toast } from "react-toastify";

import { Path, getNetworkCurrency } from "../../../static/Constants";
import { networkID } from "../../../static/utils";

import {
  AdminLayout,
  TaxStatsCards,
  TaxConfigForm,
} from "../../Components/dashboard";

import { SuccessPopup, FailedPopup } from "../../Components/ui";

import cinstance from "../../Service/randomcinstance";
import web3 from "../../Service/web3";

const LOCAL_STORAGE_TAX_KEY = "autobet_simple_tax_config";

const withTimeout = (promise, ms = 3500) => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Request timed out")), ms)
    ),
  ]);
};

function AdminTaxation() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [adminAccount, setAdminAccount] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [currency, setCurrency] = useState(getNetworkCurrency(networkID));

  // Simple Government Tax Form State
  const [formData, setFormData] = useState({
    taxRate: 10,
    taxWallet: "",
  });

  // Success / Failure Modal State
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showFailModal, setShowFailModal] = useState(false);
  const [failMessage, setFailMessage] = useState("");

  // Load initial settings
  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      setLoading(true);

      // 1. Load from localStorage
      try {
        const saved = localStorage.getItem(LOCAL_STORAGE_TAX_KEY);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (
            parsed.taxWallet &&
            (parsed.taxWallet.toLowerCase() === "0x89205a3a3b2a69de6dbf7f01ed13b2108b2c43e7" ||
              parsed.taxWallet === "0x00000000000000000000000000000000000000000000")
          ) {
            parsed.taxWallet = "";
            localStorage.setItem(LOCAL_STORAGE_TAX_KEY, JSON.stringify(parsed));
          }
          if (isMounted) {
            setFormData((prev) => ({ ...prev, ...parsed }));
          }
        }
      } catch (err) {
        console.warn("Could not read tax config:", err);
      }

      // 2. Check Web3 credentials
      if (!window.ethereum && !web3) {
        if (isMounted) {
          setAuthChecked(true);
          setLoading(false);
        }
        return;
      }

      try {
        let accounts = [];
        if (window.ethereum?.request) {
          accounts = await withTimeout(
            window.ethereum.request({ method: "eth_accounts" }),
            2000
          ).catch(() => []);
        }
        if (!accounts || accounts.length === 0) {
          accounts = await withTimeout(web3.eth.getAccounts(), 2000).catch(() => []);
        }

        const currentAccount = accounts?.[0] || window.ethereum?.selectedAddress || "";
        if (isMounted && currentAccount) {
          setAdminAccount(currentAccount);
        }

        let contractAdmin = "";
        try {
          if (cinstance?.methods?.admin) {
            contractAdmin = await withTimeout(cinstance.methods.admin().call(), 3000);
          }
        } catch (err) {
          console.warn("Could not fetch contract admin:", err);
        }

        const authorized = Boolean(
          contractAdmin &&
          currentAccount &&
          contractAdmin.toLowerCase() === currentAccount.toLowerCase()
        );

        if (isMounted) {
          setIsAdmin(authorized);
          setAuthChecked(true);
        }

        // Try reading on-chain fee percentage if available
        if (cinstance?.methods?.transferFeePerc) {
          try {
            const onChainFee = await cinstance.methods.transferFeePerc().call();
            if (onChainFee && isMounted) {
              setFormData((prev) => ({
                ...prev,
                taxRate: Number(onChainFee) || prev.taxRate,
              }));
            }
          } catch (e) {
            console.warn("Could not read transferFeePerc:", e);
          }
        }
      } catch (e) {
        console.error("Government Taxation init error:", e);
        if (isMounted) setAuthChecked(true);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    init();

    return () => {
      isMounted = false;
    };
  }, []);

  // Save tax handler
  const handleSaveTax = () => {
    const rate = parseFloat(formData.taxRate);
    if (isNaN(rate) || rate < 0 || rate > 100) {
      toast.error("Please enter a valid government tax percentage between 0% and 100%.");
      return;
    }
    if (!formData.taxWallet || !formData.taxWallet.startsWith("0x")) {
      toast.error("Please enter a valid government remittance wallet address (0x...).");
      return;
    }

    setSaving(true);
    try {
      localStorage.setItem(LOCAL_STORAGE_TAX_KEY, JSON.stringify(formData));
      setSuccessMessage(`Government tax rate set to ${rate}% and saved successfully. This statutory tax is collected for the government and is independent from platform revenue.`);
      setShowSuccessModal(true);
      toast.success("Government tax settings saved successfully!");
    } catch (err) {
      toast.error("Failed to save government tax settings.");
    } finally {
      setSaving(false);
    }
  };

  // Sync to smart contract
  const handleSyncContract = async () => {
    if (!window.ethereum || !web3) {
      toast.warning("Web3 wallet not detected. Connect MetaMask to sync on-chain.");
      return;
    }

    try {
      setSyncing(true);
      let accounts = [];
      if (window.ethereum?.request) {
        accounts = await window.ethereum.request({ method: "eth_accounts" });
      }
      if (!accounts || accounts.length === 0) {
        accounts = await web3.eth.getAccounts();
      }
      const userAccount = accounts?.[0] || adminAccount;

      if (!userAccount) {
        toast.error("No active Web3 account found. Please unlock MetaMask.");
        setSyncing(false);
        return;
      }

      const rateInt = Math.floor(Number(formData.taxRate) || 10);

      if (cinstance?.methods?.updateFee) {
        toast.info("Submitting government tax update to blockchain...");
        await cinstance.methods
          .updateFee(10, rateInt, 5, 1, 1)
          .send({ from: userAccount });
      }

      localStorage.setItem(LOCAL_STORAGE_TAX_KEY, JSON.stringify(formData));
      setSuccessMessage(`Government statutory tax rate of ${rateInt}% synchronized to smart contract!`);
      setShowSuccessModal(true);
      toast.success("Government tax rate synced to blockchain successfully!");
    } catch (err) {
      console.error("Contract sync error:", err);
      setFailMessage(err.message || "Failed to broadcast tax updates to the blockchain.");
      setShowFailModal(true);
      toast.error("Sync failed or rejected by user.");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <AdminLayout
      title="Government Taxation"
      subtitle="Set the statutory government tax rate and official remittance wallet. This tax is collected by the government and is independent of platform revenue."
      breadcrumbs={[
        { label: "Dashboard", href: Path.admin },
        { label: "Government Tax" },
      ]}
      creatorAddress={adminAccount}
      authChecked={authChecked}
      isAuthorized={isAdmin}
      accessDeniedProps={{
        message: "You must be an administrator to access the Government Taxation console.",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, maxWidth: 900 }}>
        {/* 1. Overview Cards */}
        <TaxStatsCards
          taxRate={formData.taxRate}
          taxWallet={formData.taxWallet}
          currency={currency}
        />

        {/* 2. Simple Set Government Tax Form */}
        <TaxConfigForm
          formData={formData}
          onChange={setFormData}
          onSave={handleSaveTax}
          onSyncContract={handleSyncContract}
          saving={saving}
          syncing={syncing}
          currency={currency}
        />
      </Box>

      {/* Popups */}
      <SuccessPopup
        open={showSuccessModal}
        title="Government Tax Saved"
        message={successMessage}
        onClose={() => setShowSuccessModal(false)}
      />

      <FailedPopup
        open={showFailModal}
        title="Action Failed"
        message={failMessage}
        onClose={() => setShowFailModal(false)}
      />
    </AdminLayout>
  );
}

export default AdminTaxation;
